import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button, Divider } from "@heroui/react";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiEdit2,
  FiDownload,
  FiLogOut,
  FiPlusCircle,
  FiRefreshCcw,
  FiX,
} from "react-icons/fi";
import AddNoteModal from "@/components/ui/UserSidebar/modals/AddNoteModal";
import { useAuth } from "@/contexts/AuthContext";
import ConfirmationModal from "@/components/modal/ConfirmationModal";
import { useTransactions } from "@/hooks/useTransactions";
import { useUserProgress } from "@/hooks/useUserProgress";
import { useUserCohort } from "@/hooks/cohort/useUserCohort";
import { useClients } from "@/hooks/client-profile/useClients";
import { useParams, useLocation } from "react-router-dom";
import { TbArrowBarToUp } from "react-icons/tb";
import { getCurrencySymbol } from "@/utils/currencies";
import CurrencyModal from "@/pages/user/CurrencyModal";
import { useTransactionSummaryExport } from "@/hooks/useTransactionSummaryExport";

import { useHeaderStore } from "@/store/headerStore";
import { useAccountCollapseStore } from "@/store/accountCollapseStore";
import { useDragContext, DragData } from "@/contexts/DragContext";
import { SnapshotService } from "@/services/SnapshotService";
import { getSessionId } from "@/utils/session";

interface UserSidebarProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserSidebar = ({ isSidebarOpen, setIsSidebarOpen }: UserSidebarProps) => {
  const { isHeaderVisible, isFitToScreen, toggleHeader, setFitToScreen } =
    useHeaderStore();
  const { collapseAllAccounts } = useAccountCollapseStore();
  const { setCurrentDragData } = useDragContext();
  const { logout, currentUser, resetAccount } = useAuth();
  const { cohortId } = useParams<{ cohortId?: string }>();
  const { cohort, loading: cohortLoading } = useUserCohort(cohortId);
  const { clients } = useClients();
  const location = useLocation();
  const { exportSummary, isExporting } = useTransactionSummaryExport();
  const showExport = location.pathname !== "/financial-statements";
  const [forceRender, setForceRender] = useState(0);

  // Force re-render when view mode changes
  useEffect(() => {
    const handleViewModeChange = () => {
      setForceRender((prev) => prev + 1);
    };

    window.addEventListener("cohortViewModeChanged", handleViewModeChange);
    return () => {
      window.removeEventListener("cohortViewModeChanged", handleViewModeChange);
    };
  }, []);
  const {
    transactions,
    currentTransactionIndex,
    currentTransaction,
    canGoNext,
    canGoPrevious,
    goToNext,
    goToPrevious,
    formatAmount,
    getDraggableAmounts,
    loading: transactionsLoading,
  } = useTransactions(cohortId);
  const {
    accountDroppedAmounts,
    liabilityDroppedAmounts,
    equityAccountDroppedAmounts,
    expenseDroppedAmounts,
    incomeDroppedAmounts,
    equityAmounts,
    resetProgress,
    companyName,
    updateCompanyName,
    saveProgress,
    isLoaded,
    getTransactionNote,
    saveTransactionNote,
  } = useUserProgress(cohortId);
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [isResetOpen, setIsResetOpen] = useState(false);
  const [usedAmounts, setUsedAmounts] = useState<Set<string>>(new Set());
  const [localCompanyName, setLocalCompanyName] = useState(companyName);
  const [dragOver, setDragOver] = useState(false);

  // Sync local company name when loaded from Firebase
  useEffect(() => {
    setLocalCompanyName(companyName);
  }, [companyName]);

  // Get the main client for the cohort
  const mainClient = clients?.find(
    (client) => client.id === cohort?.selectedClients?.[0]?.clientId,
  );

  // Removed unused beforeunload event listener to prevent memory leak

  const handleToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Wrapper functions for navigation that also collapse accounts
  const handleGoToNext = useCallback(() => {
    collapseAllAccounts();
    // Ask dashboard to save snapshot for current stage, then move next
    const evt = new Event("saveSnapshotAndNext");
    window.dispatchEvent(evt);
  }, [collapseAllAccounts]);

  // Jump straight to a transaction from the picker. Goes through the dashboard
  // (same as Previous / Next) so the current stage's snapshot is saved first.
  const handleGoToIndex = useCallback(
    (index: number) => {
      if (index === currentTransactionIndex) return;
      collapseAllAccounts();
      window.dispatchEvent(
        new CustomEvent("saveSnapshotAndGoTo", { detail: index }),
      );
    },
    [collapseAllAccounts, currentTransactionIndex],
  );

  const handleGoToPrevious = useCallback(() => {
    collapseAllAccounts();
    const evt = new Event("saveSnapshotAndPrev");
    window.dispatchEvent(evt);
  }, [
    currentTransactionIndex,
    transactions.length,
    canGoPrevious,
    currentTransaction?.id,
    collapseAllAccounts,
  ]);

  // Wrapper function for header toggle that also collapses accounts and manages fit-to-screen
  const handleToggleHeader = () => {
    collapseAllAccounts();

    // Use direct state setters for more predictable behavior
    if (isHeaderVisible) {
      setFitToScreen(true); // Enable fit-to-screen when hiding header
    } else {
      setFitToScreen(false); // Disable fit-to-screen when showing header
    }
    toggleHeader();
  };

  const handleResetAccount = async () => {
    try {
      await resetAccount();
      // Reset local dashboard state
      resetProgress();
      // Also remove all snapshots for this user+cohort+current session
      try {
        const sid = getSessionId();
        if (currentUser?.id && cohort?.id) {
          await SnapshotService.deleteSnapshots({
            userId: currentUser.id,
            cohortId: cohort.id,
            sessionId: sid || undefined,
          });
        }
      } catch (e) {
        console.warn("Failed to delete snapshots during reset", e);
      }
      // Reset local state
      setIsResetOpen(false);
      // Force reload the page to ensure all state is cleared
      window.location.reload();
    } catch (error) {
      console.error("Failed to reset account:", error);
    }
  };

  const currencyCode = (currentUser?.currency || "GBP").toUpperCase();
  const currencySymbol = getCurrencySymbol(currentUser?.currency);

  // Memoized calculation of used amounts - only recalculates when dependencies change
  const calculatedUsedAmounts = useMemo(() => {
    // Don't calculate used amounts until data is fully loaded
    if (!isLoaded) {
      return new Set<string>();
    }

    // Expected |amount| per configured transaction. Transaction ids are
    // positional (index + 1), so when a transaction is deleted and another is
    // added later, old dropped amounts can carry the same transactionId as the
    // new transaction. A dropped amount whose value no longer matches its
    // transaction is that kind of stale leftover and must not grey out the
    // current transaction's draggable amounts.
    const expectedByTxId = new Map<number, number>();
    (transactions || []).forEach((t) => {
      const v = Math.abs(parseFloat(t.amount));
      if (!isNaN(v)) expectedByTxId.set(Number(t.id), v);
    });

    const newUsedAmounts = new Set<string>();
    const markUsed = (amount: any) => {
      const expected = expectedByTxId.get(Number(amount.transactionId));
      if (
        expected !== undefined &&
        Math.abs(amount.amount || 0) !== expected
      ) {
        return; // stale amount from a deleted/replaced transaction
      }
      // Track both the type and transaction ID
      newUsedAmounts.add(`${amount.type}-${amount.transactionId}`);
    };

    // Optimized helper function to check dropped amounts
    const checkDroppedAmounts = (droppedAmountsMap: Record<string, any[]>) => {
      Object.values(droppedAmountsMap).forEach((amounts) => {
        amounts.forEach(markUsed);
      });
    };

    // Check all containers for used amounts
    checkDroppedAmounts(accountDroppedAmounts);
    checkDroppedAmounts(liabilityDroppedAmounts);
    checkDroppedAmounts(equityAccountDroppedAmounts);
    checkDroppedAmounts(expenseDroppedAmounts || {});
    checkDroppedAmounts(incomeDroppedAmounts || {});

    // Check equity amounts
    equityAmounts.forEach(markUsed);

    return newUsedAmounts;
  }, [
    isLoaded,
    transactions,
    accountDroppedAmounts,
    liabilityDroppedAmounts,
    equityAccountDroppedAmounts,
    expenseDroppedAmounts,
    incomeDroppedAmounts,
    equityAmounts,
  ]);

  // Update state only when the memoized calculation changes
  useEffect(() => {
    setUsedAmounts(calculatedUsedAmounts);
  }, [calculatedUsedAmounts]);

  // Fix memory leak: Move event handlers inside useEffect to avoid dependency issues
  useEffect(() => {
    // Create stable event handler references inside effect
    const handleAmountRemoved = (event: CustomEvent) => {
      const { type, transactionId } = event.detail;
      const amountId = `${type}-${transactionId}`;

      setUsedAmounts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(amountId);
        return newSet;
      });
    };

    const handleAmountUsed = (event: CustomEvent) => {
      const { type, transactionId } = event.detail;
      const amountId = `${type}-${transactionId}`;

      setUsedAmounts((prev) => {
        const newSet = new Set(prev);
        newSet.add(amountId);
        return newSet;
      });
    };

    // Add event listeners
    window.addEventListener(
      "amountRemoved",
      handleAmountRemoved as EventListener,
    );
    window.addEventListener("amountUsed", handleAmountUsed as EventListener);

    return () => {
      // Cleanup: Remove exact same function references
      window.removeEventListener(
        "amountRemoved",
        handleAmountRemoved as EventListener,
      );
      window.removeEventListener(
        "amountUsed",
        handleAmountUsed as EventListener,
      );
    };
  }, []); // Empty dependency array - stable references, no re-registration

  const handleDragStart = useCallback(
    (
      e: React.DragEvent,
      amount: number,
      type: string,
      transactionId: number,
      date: string,
    ) => {
      // Check if this amount has already been used
      const amountId = `${type}-${transactionId}`;
      if (usedAmounts.has(amountId)) {
        e.preventDefault();
        return;
      }

      // Find the item to get its color
      const draggedItem = getDraggableAmounts().find(
        (item) => item.amount === amount && item.type === type,
      );
      const isPositive = draggedItem?.color === "sky"; // sky is positive, red is negative
      const signedAmount = isPositive ? amount : -amount;

      const dragData: DragData = {
        id: `${type}-${transactionId}-${Date.now()}`, // Ensure unique ID
        amount: signedAmount,
        type: type,
        date: date,
        transactionId: transactionId,
        formattedAmount: formatAmount(signedAmount),
        originalColor: draggedItem?.color === "sky" ? "sky" : "danger",
        isPositive: isPositive,
        details: draggedItem?.details || "",
      };

      // Use state-driven CSS class instead of direct DOM manipulation
      e.currentTarget.classList.add("dragging-amount");

      // Store the drag data securely in context
      setCurrentDragData(dragData);

      e.dataTransfer.setData("application/json", JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = "move";
      // Use the same simple approach as AccountBox
      e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    },
    [usedAmounts, getDraggableAmounts, formatAmount, setCurrentDragData],
  );

  const handleDragEnd = useCallback(
    (e: React.DragEvent) => {
      // Remove state-driven CSS class
      e.currentTarget.classList.remove("dragging-amount");

      // Clean up the drag data from context
      setCurrentDragData(null);
    },
    [setCurrentDragData],
  );

  // Drop zone handlers for returning amounts to sidebar
  const handleSidebarDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only accept drops from account boxes (amounts with sourceAccountId)
    try {
      const dragDataText = e.dataTransfer.getData("application/json");
      if (dragDataText) {
        const data = JSON.parse(dragDataText);
        if (data.sourceAccountId && data.type !== "account") {
          e.dataTransfer.dropEffect = "move";
          setDragOver(true);
        }
      }
    } catch (error) {
      // Ignore parsing errors
    }
  }, []);

  const handleSidebarDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleSidebarDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    try {
      const dragDataText = e.dataTransfer.getData("application/json");
      if (!dragDataText) return;

      const data = JSON.parse(dragDataText);

      // Only handle amounts being returned from account boxes
      if (data.sourceAccountId && data.type !== "account") {
        // Determine the correct type based on the original amount's properties
        const actualType = data.isPositive ? "debit" : "credit";

        // Dispatch the removal event to notify the Dashboard
        const event = new CustomEvent("amountRemoved", {
          detail: { type: actualType, transactionId: data.transactionId },
        });
        window.dispatchEvent(event);

        // Also dispatch a custom event to trigger account box cleanup
        const cleanupEvent = new CustomEvent("amountReturnedToSidebar", {
          detail: {
            sourceAccountId: data.sourceAccountId,
            amountId: data.id,
            transactionId: data.transactionId,
            type: actualType,
          },
        });
        window.dispatchEvent(cleanupEvent);
      }
    } catch (error) {
      console.error("Error handling sidebar drop:", error);
    }
  }, []);

  return (
    <div
      className={`fixed top-0 z-40 h-full w-full max-w-[260px] 2xl:max-w-[290px] min-1660:max-w-[320px] shadow-md p-4 2xl:p-6 overflow-y-auto transition-transform duration-300 ease-in-out transform bg-white ${
        isSidebarOpen
          ? "lg:translate-x-0"
          : "lg:translate-x-0 -translate-x-full"
      } ${dragOver ? "ring-2 ring-blue-400 ring-offset-2 bg-blue-50/30" : ""}`}
      onDragOver={handleSidebarDragOver}
      onDragLeave={handleSidebarDragLeave}
      onDrop={handleSidebarDrop}
    >
      <div className="h-full flex flex-col justify-between">
        <div>
          <div>
            {/* <img
              src={mainClient?.fullLogoUrl || "/assets/images/logo/logo.png"}
              alt={mainClient?.name || "AME Logo"}
              className="h-8 xl:h-11 w-auto mb-2 xl:mb-4"
            /> */}

            <div className="flex items-center justify-end mb-2 xl:mb-4">
              {mainClient?.iconLogoUrl && (
                <img
                  src={mainClient.iconLogoUrl}
                  alt={`${mainClient.name} Logo`}
                  className="h-9 w-auto mr-auto"
                />
              )}

              <button
                onClick={handleToggleHeader}
                className="p-1 rounded-md hover:bg-gray-100 focus:outline-none ring-2 ring-gray-300 transition-colors duration-200"
                aria-label={isHeaderVisible ? "Fit to screen" : "Show header"}
              >
                <TbArrowBarToUp
                  className={`text-[#98A2B3] transition-transform duration-300 ${
                    isHeaderVisible ? "rotate-0" : "rotate-180"
                  }`}
                  size={20}
                />
              </button>
            </div>
            <button
              onClick={() => handleToggle()}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 lg:hidden"
            >
              <FiX size={20} />
            </button>
          </div>
          <Divider className="bg-gray-200" />

          {!isLoaded ? (
            <div className="my-4 flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
              <span className="text-gray-500 text-sm">
                Loading company data...
              </span>
            </div>
          ) : (
            <input
              type="text"
              value={localCompanyName}
              onChange={(e) => {
                setLocalCompanyName(e.target.value);
              }}
              onBlur={(e) => {
                updateCompanyName(e.target.value);
              }}
              className="h-2md:my-0 xl:my-3 text-lg h-2md:text-base font-bold leading-none text-gray-500 border-none h-2md:p-2 2xl:p-2 rounded w-full focus:outline-none focus:ring-2 focus:ring-blue-50"
              placeholder="Enter Company Name"
            />
          )}

          {/* <input
            type="text"
            value={
              cohortLoading
                ? "Loading..."
                : cohort?.name || "No Cohort Assigned"
            }
            readOnly
            className="xl:my-4 text-md xl:text-xl font-bold leading-none text-gray-700 border-none p-1 xl:p-2 rounded w-full focus:outline-none bg-transparent cursor-default"
            placeholder="Cohort Name"
          /> */}

          <Divider className="bg-gray-200 " />

          <div className="bg-white w-full inline-flex overflow-hidden rounded-lg border border-gray-300 divide-x divide-gray-300 h-2md:my-3 h-2md:mt-4 my-2 xl:my-6">
            <button
              onClick={handleGoToPrevious}
              disabled={!canGoPrevious || transactionsLoading}
              className="bg-white focus:outline-none w-full justify-center rounded-none border-0 flex items-center gap-2 px-4 py-1 xl:py-2 h-2md:py-1 h-2md:text-sm text-sm xl:text-[14px] text-gray-700 font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiArrowLeft className="text-md" />
              Previous
            </button>
            <button
              onClick={handleGoToNext}
              disabled={!canGoNext || transactionsLoading}
              className="bg-white w-full focus:outline-none justify-center rounded-none border-0 flex items-center gap-2 px-4 py-1 xl:py-2 h-2md:py-1 h-2md:text-sm text-sm xl:text-[14px] text-gray-700 font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <FiArrowRight className="text-md" />
            </button>
          </div>

          <div>
            {transactionsLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-500 text-sm">Loading transactions...</p>
              </div>
            ) : currentTransaction ? (
              <>
                <h3 className="text-gray-700 font-bold text-sm xl:text-[15px] h-2md:text-sm">
                  Transaction{" "}
                  <span className="text-gray-400 ml-2">
                    #{currentTransaction.id}
                  </span>
                  {/* Picker so any transaction can be jumped to directly */}
                  <span className="text-xs text-blue-600 ml-2 inline-flex items-center gap-1 align-middle">
                    (
                    <select
                      value={currentTransactionIndex}
                      onChange={(e) => handleGoToIndex(Number(e.target.value))}
                      disabled={transactionsLoading || transactions.length === 0}
                      aria-label="Go to transaction"
                      title="Go to transaction"
                      className="cursor-pointer rounded border border-blue-200 bg-white px-1 py-0.5 text-xs font-semibold text-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {transactions.map((transaction, index) => (
                        <option key={transaction.id ?? index} value={index}>
                          {index + 1}
                        </option>
                      ))}
                    </select>
                    of {transactions.length})
                  </span>
                </h3>
                <div className="flex gap-2 my-2">
                  <FiCalendar className="xl:text-[15px] text-sm h-2md:text-[15px]" />
                  <span className="text-[14px] h-2md:text-[12px] text-gray-500">
                    {currentTransaction.date}
                  </span>
                </div>
                {/*<div className="text-xs text-gray-400 xl:mb-2">*/}
                {/*  From: {currentTransaction.cohortName}*/}
                {/*</div>*/}
                <p className="text-[13px] mt-2 leading-5 text-slate-700">
                  {currentTransaction.details}
                </p>
              </>
            ) : (
              <div className="text-center py-4">
                <h3 className="text-gray-700 font-bold text-lg mb-2">
                  No Transactions
                </h3>
                <p className="text-gray-500 text-sm">
                  No transactions found in your cohorts.
                </p>
              </div>
            )}

            <Button
              onPress={() => setIsNoteOpen(true)}
              className="flex items-center focus:outline-none gap-2 w-full bg-transparent rounded-lg border-gray-300 font-bold text-sm !text-gray-700 mt-2 xl:mt-6 !h-8 xl:!h-10 h-2md:!h-8 h-2md:mt-2 hover:border-[#0BA5EC]"
            >
              {currentTransaction &&
              getTransactionNote(currentTransaction.id)?.length > 0 ? (
                <FiEdit2 className="xl:text-lg text-sm h-2md:text-sm" />
              ) : (
                <FiPlusCircle className="xl:text-lg text-sm h-2md:text-sm" />
              )}
              {currentTransaction &&
              getTransactionNote(currentTransaction.id)?.length > 0
                ? "View/Edit Note"
                : "Add Note"}
            </Button>
          </div>

          <Divider className="bg-gray-200 h-0.5 mb-2 mt-3 xl:mb-4 xl:mt-5 h-2md:mb-2 h-2md:mt-3" />

          <section className="xl:mt-6 h-2md:mt-0 max-w-full text-sm rounded-lg w-full">
            <p className="text-[13px] leading-5 text-slate-700">
              Drag & drop the amounts below into the correct account
            </p>

            <div className="overflow-hidden p-1 xl:p-4 h-2md:p-1 h-2md:mt-2 mt-2 xl:mt-4 w-full font-bold leading-none whitespace-nowrap bg-gray-50 rounded-lg border border-dashed border-gray-300 space-y-1 xl:space-y-3">
              {currentTransaction ? (
                getDraggableAmounts().map((item) => {
                  const amountId = `${item.type}-${currentTransaction.id}`;
                  const isUsed = usedAmounts.has(amountId);
                  return (
                    <div
                      // key={item.id}
                      key={`${amountId}-${isUsed ? "used" : "available"}`}
                      draggable={!isUsed}
                      onDragStart={(e) => {
                        if (isUsed) {
                          e.preventDefault();
                          return;
                        }
                        handleDragStart(
                          e,
                          item.amount,
                          item.type,
                          currentTransaction.id,
                          item.date,
                        );
                      }}
                      onDragEnd={handleDragEnd}
                      className={`flex-1 px-4 py-1.5 h-2md:py-1.5 xl:py-2.5 text-center draggable-item select-none ${
                        isUsed
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-grab active:cursor-grabbing"
                      } ${
                        item.color === "sky"
                          ? "bg-[#F3FBFF] border-2 border-sky-light border-l-[8px] border-l-primary-sky text-sky-dark"
                          : "bg-[#FFF5F6] border-2 border-[#FF888C] border-l-[8px] border-l-danger-dark text-danger-color"
                      } rounded-lg shadow-sm`}
                    >
                      {formatAmount(
                        item.color === "red" ? -item.amount : item.amount,
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-4 text-gray-500">
                  No amounts to drag
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="">
          <div className="flex items-center gap-2 xl:mt-6 h-2md:mt-2">
            <Button
              onPress={() => setIsCurrencyOpen(true)}
              title="Change currency"
              className="flex-1 flex items-center justify-center focus:outline-none gap-2 bg-transparent rounded-lg border border-gray-300 font-bold text-sm !text-gray-700 !h-8 xl:!h-10 h-2md:!h-8"
            >
              {currencyCode} {currencySymbol}
            </Button>

            {showExport && (
              <Button
                onPress={() => exportSummary()}
                isLoading={isExporting}
                title="Export"
                className="flex-1 flex items-center justify-center focus:outline-none gap-2 bg-transparent rounded-lg border border-gray-300 font-bold text-sm !text-gray-700 !h-8 xl:!h-10 h-2md:!h-8"
              >
                <FiDownload className="xl:text-lg text-md" />
                Export
              </Button>
            )}
          </div>

          <Button
            onPress={() => setIsResetOpen(true)}
            className="flex focus:outline-none items-center gap-3 w-full bg-transparent rounded-lg border-gray-300 font-bold text-sm !text-gray-700 mt-2 !h-8 xl:!h-10 h-2md:!h-8"
          >
            <FiRefreshCcw className="xl:text-xl text-md" />
            Reset Accounts
          </Button>

          <Divider className="bg-gray-200 h-0.5 my-2 xl:my-4 h-2md:my-2 h-2md:mt-3" />

          <div className="flex gap-2 justify-between items-center pl-2 xl:mt-4 h-2md:mt-0 w-full">
            <span className="self-stretch my-auto text-gray-900 text-xs font-bold truncate min-w-0 flex-1">
              {currentUser?.name}
            </span>

            <Button
              className="w-auto focus:outline-none !p-0 flex items-center gap-3 bg-transparent hover:border-transparent border-transparent font-bold text-xs !text-gray-700 !h-8 max-h-[866px]:!h-20 xl:!h-10 h-2md:!h-8 shrink-0"
              onPress={() => setIsLogoutOpen(true)}
            >
              Logout
              <FiLogOut size={20} className="font-bold" />
            </Button>
          </div>
        </div>
      </div>

      <AddNoteModal
        isOpen={isNoteOpen}
        onOpenChange={setIsNoteOpen}
        currentTransaction={currentTransaction}
        onSaveNote={saveTransactionNote}
        getTransactionNote={getTransactionNote}
      />
      <ConfirmationModal isOpen={isLogoutOpen} onOpenChange={setIsLogoutOpen}>
        <div className="text-lg font-semibold mb-3">
          Are you Sure want to Logout?
        </div>
        <div className="flex justify-end  gap-2">
          <Button onPress={() => setIsLogoutOpen(false)}>No</Button>
          <Button
            variant="solid"
            className="bg-grayColor text-white"
            onPress={() => logout()}
          >
            Yes
          </Button>
        </div>
      </ConfirmationModal>

      <ConfirmationModal isOpen={isResetOpen} onOpenChange={setIsResetOpen}>
        <div className="text-lg font-semibold mb-3">
          Would you like to reset your accounts and transactions?
        </div>
        <div className="text-sm text-gray-500 mb-4">
          This will remove all your accounts, transactions, and return you to
          the currency selection screen.
        </div>
        <div className="flex justify-end gap-2">
          <Button onPress={() => setIsResetOpen(false)}>Cancel</Button>
          <Button
            variant="solid"
            className="bg-red-600 text-white"
            onPress={handleResetAccount}
          >
            Reset Everything
          </Button>
        </div>
      </ConfirmationModal>

      <CurrencyModal
        externalOpen={isCurrencyOpen}
        onExternalClose={() => setIsCurrencyOpen(false)}
      />
    </div>
  );
};

export default UserSidebar;
